import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { sendMutation } from "@/lib/offlineQueue";
import { useAuth } from "@/contexts/AuthContext";
import { LiveTimer } from "@/components/LiveTimer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Plane, Square, Play, AlertTriangle, MapPin, DollarSign, Zap, Star, Sun, Sunrise, ClipboardList, CalendarOff, Wrench, Search } from "lucide-react";
import { toast } from "sonner";
import { formatMSTDateTime, minutesToHours } from "@/lib/timeUtils";
import InstallHint from "@/components/InstallHint";
import PostInstallPushPrompt from "@/components/PostInstallPushPrompt";

const fleetOrder = ["AVRO RJ85", "AVRO RJ100", "DASH 8", "ATR72", "DORNIER 228", "SKYVAN"];

export default function EngineerDashboard() {
  const { user, refresh: refreshUser } = useAuth();
  const [aircraft, setAircraft] = useState([]);
  const [active, setActive] = useState(null);
  const [duty, setDuty] = useState(null);
  const [dutyBusy, setDutyBusy] = useState(false);
  // Location help pop-up — opened whenever a clock-in / duty start is blocked
  // because GPS is off, denied or unavailable. `retry` re-runs the blocked action.
  const [locHelp, setLocHelp] = useState({ open: false, reason: null, busy: false, attempted: false, action: "clock in", retry: null });
  // Geolocation permission state — "granted" hides the turn-on banner,
  // "prompt"/"denied" show it. Live-updates via the Permissions API onchange.
  const [locPerm, setLocPerm] = useState(null);
  useEffect(() => {
    if (!navigator.permissions?.query) return;
    let status;
    navigator.permissions.query({ name: "geolocation" })
      .then((s) => {
        status = s;
        setLocPerm(s.state);
        s.onchange = () => setLocPerm(s.state);
      })
      .catch(() => {});
    return () => { if (status) status.onchange = null; };
  }, []);
  // Auto-fire the browser's NATIVE location prompt on login/dashboard load
  // while the permission is still undecided ("prompt"). Once per tab session
  // so navigating back to the dashboard doesn't re-nag.
  useEffect(() => {
    if (locPerm !== "prompt") return;
    if (sessionStorage.getItem("st.loc_autoprompt")) return;
    sessionStorage.setItem("st.loc_autoprompt", "1");
    getCurrentPosition();
  }, [locPerm]);
  const [sessions, setSessions] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startingTail, setStartingTail] = useState(null);
  const [mode, setMode] = useState({ is_ot: false, is_acting_crew_chief: false, is_work_on_stat: false });
  const [statOption, setStatOption] = useState("AS"); // AN | AO | AP | AS
  const [statOptions, setStatOptions] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskModal, setTaskModal] = useState({ open: false, tail: null, task_type: "", notes: "", capex_code: null, capex_note: "" });
  const [capexModal, setCapexModal] = useState({ open: false, tail: null, status: null, codes: [], loading: false, choice: "", search: "", note: "", mandatoryReason: "aircraft-status" });
  const [wholeDayModal, setWholeDayModal] = useState({ open: false, notes: "", submitting: false });
  // OT confirmation modal — surfaced when /clock/whole-day returns 409 because
  // the user already has hours logged today. Confirmation resubmits with
  // `is_ot: true` so the second fill is booked as Overtime.
  const [otConfirm, setOtConfirm] = useState({ open: false, existing_hours: 0, would_add_hours: 0, notes: "", submitting: false });
  const [timeOffModal, setTimeOffModal] = useState({ open: false, type: "vacation", start_date: new Date().toISOString().slice(0, 10), end_date: new Date().toISOString().slice(0, 10), notes: "", hours: "", submitting: false });
  // Persistent Admin/Training balance note — stored per-user in localStorage.
  // When set, it's attached automatically to any clock-out where the day ends
  // below the user's daily target (so the AC shortfall auto-fill picks it up).
  // Replaces the old modal-prompt-on-stop UX.
  const acNoteKey = user?.id ? `st.ac_note.${user.id}` : null;
  const [acNote, setAcNote] = useState(() => (acNoteKey ? localStorage.getItem(acNoteKey) || "" : ""));
  useEffect(() => {
    if (acNoteKey) setAcNote(localStorage.getItem(acNoteKey) || "");
  }, [acNoteKey]);
  const persistAcNote = (v) => {
    setAcNote(v);
    if (acNoteKey) localStorage.setItem(acNoteKey, v);
  };
  // Today (MST) — computed once for the stat-holiday gate & banner.
  const todayMst = React.useMemo(() => {
    // "en-CA" locale in America/Denver gives us YYYY-MM-DD reliably.
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
  }, []);
  const [todayStatName, setTodayStatName] = useState(null); // e.g. "Canada Day" | null

  useEffect(() => {
    api.get("/work-on-stat-options").then((r) => setStatOptions(r.data)).catch(() => {});
    api.get("/task-types").then((r) => setTaskTypes(r.data)).catch(() => {});
  }, []);

  // Detect if today (MST) is a stat holiday for the user's location. Runs once
  // when the user profile is loaded and re-runs if the location changes.
  useEffect(() => {
    if (!user?.location) { setTodayStatName(null); return; }
    api.get(`/stat-holidays?location=${encodeURIComponent(user.location)}`)
      .then((r) => {
        const match = (r.data || []).find((h) => h.date === todayMst);
        setTodayStatName(match?.name || null);
      })
      .catch(() => setTodayStatName(null));
  }, [user?.location, todayMst]);

  // If today IS a stat holiday and there's no active session, force the mode
  // to Work-on-Stat — the user cannot start Regular / OT / Acting-CC anyway.
  useEffect(() => {
    if (todayStatName && !active && !mode.is_work_on_stat) {
      setMode({ is_ot: false, is_acting_crew_chief: false, is_work_on_stat: true });
    }
  }, [todayStatName, active, mode.is_work_on_stat]);

  const refreshAll = async () => {
    const [acR, peR] = await Promise.all([
      api.get("/aircraft"),
      api.get("/pay-periods/current"),
    ]);
    setAircraft(acR.data);
    setCurrentPeriod(peR.data);
    const [acS, seS, duS] = await Promise.all([
      api.get("/clock/active"),
      api.get(`/clock/sessions?period=${peR.data.period_number}`),
      api.get("/duty/current"),
    ]);
    setActive(acS.data || null);
    setSessions(seS.data || []);
    setDuty(duS.data?.active || null);
  };

  useEffect(() => { refreshAll(); }, []);

  // Sync duty state when the login "Start duty?" alert starts a session.
  useEffect(() => {
    const onDutyChanged = () => api.get("/duty/current").then((r) => setDuty(r.data?.active || null)).catch(() => {});
    window.addEventListener("st:duty-changed", onDutyChanged);
    return () => window.removeEventListener("st:duty-changed", onDutyChanged);
  }, []);

  // NOTE: the 30-minute GPS heartbeat now lives in the global
  // <LocationPingHeartbeat /> (AppShell) so pings fire from any page —
  // this dashboard only nudges it via the "st:ping-now" event on session start.

  const start = async (tail) => {
    if (!user?.location) {
      toast.error("Set your Location of Work in your profile before clocking in.");
      return;
    }
    // If the aircraft is on Heavy Check / CAPEX, force the CAPEX-code picker
    // + mandatory note before the normal task-type flow. Backend re-validates.
    // Manual CAPEX selection now flows through the regular task-type modal
    // (user picks task_type = "CAPEX" → nested CAPEX code picker appears).
    const acDoc = aircraft.find((a) => a.tail === tail);
    // Only Heavy Check / Down-for-CAPEX statuses force the code picker —
    // Unserviceable / MEL / Offline clock in through the normal flow.
    const autoCapex = acDoc && ["heavy_check", "capex"].includes(acDoc.status);
    if (autoCapex) {
      setCapexModal({
        open: true, tail,
        status: acDoc.status,
        codes: [], loading: true, choice: "", search: "", note: "",
        mandatoryReason: "aircraft-status",
      });
      try {
        const { data } = await api.get(`/capex-codes?tail=${encodeURIComponent(tail)}`);
        setCapexModal((m) => ({ ...m, codes: data, loading: false }));
      } catch (e) {
        setCapexModal((m) => ({ ...m, loading: false }));
        toast.error("Failed to load CAPEX codes");
      }
      return;
    }
    // Work on Stat has its own selector — no task-type required
    if (mode.is_work_on_stat) {
      await performStart(tail, null, null, null, null);
      return;
    }
    if (taskTypes.length === 0) {
      toast.error("No task types configured. Ask an admin to add them.");
      return;
    }
    // Default task = first non-CAPEX task so admins/users don't accidentally
    // fire the code picker on every clock-in.
    const defaultTaskId = (taskTypes.find((t) => t.id !== "capex") || taskTypes[0])?.id || "";
    setTaskModal({
      open: true, tail,
      task_type: defaultTaskId,
      notes: "",
      capex_code: null,
      capex_note: "",
      capex_codes: [],
      capex_codes_loading: false,
      capex_search: "",
    });
  };

  // After the user confirms a CAPEX code, either open the task-type modal (regular)
  // or proceed straight to performStart (work-on-stat mode). The code + note are
  // threaded through both paths.
  const confirmCapex = () => {
    const { tail, choice, note } = capexModal;
    if (!choice) { toast.error("Pick a CAPEX code"); return; }
    const cleanNote = (note || "").trim();
    if (!cleanNote) { toast.error("A CAPEX note is required — describe the work briefly."); return; }
    setCapexModal({ open: false, tail: null, status: null, codes: [], loading: false, choice: "", search: "", note: "", mandatoryReason: "aircraft-status" });
    // Aircraft-status gate → always Work-on-Stat OR straight to performStart
    // (no task-type picker needed because the mode column is already fixed).
    if (mode.is_work_on_stat) {
      performStart(tail, "capex", null, choice, cleanNote);
      return;
    }
    performStart(tail, "capex", null, choice, cleanNote);
  };

  // Lazy-load CAPEX codes when the user picks "CAPEX" inside the task-type
  // modal. Cached on the modal state so we don't refetch on every keystroke.
  const loadCapexCodesForTaskModal = async (tail) => {
    setTaskModal((m) => ({ ...m, capex_codes_loading: true }));
    try {
      const { data } = await api.get(`/capex-codes?tail=${encodeURIComponent(tail)}`);
      setTaskModal((m) => ({ ...m, capex_codes: data, capex_codes_loading: false }));
    } catch (e) {
      setTaskModal((m) => ({ ...m, capex_codes_loading: false }));
      toast.error("Failed to load CAPEX codes");
    }
  };

  // Request the device's current position (Promise-wrapped). Resolves to
  // {latitude, longitude} on success, or {reason: 'denied' | 'unavailable' | 'unsupported'}
  // otherwise. Never throws — geofence-bypass is graceful.
  const getCurrentPosition = () => new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ reason: "unsupported" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      (err) => resolve({ reason: err.code === 1 ? "denied" : "unavailable" }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  });

  // "I've turned it on — try again" inside the location help pop-up. Re-checks
  // GPS; on success closes the pop-up and re-runs the blocked action.
  const retryLocationHelp = async () => {
    setLocHelp((m) => ({ ...m, busy: true }));
    const pos = await getCurrentPosition();
    if (pos.latitude != null && pos.longitude != null) {
      setLocPerm("granted");
      const retry = locHelp.retry;
      setLocHelp({ open: false, reason: null, busy: false, attempted: false, action: "clock in", retry: null });
      if (retry) retry();
    } else {
      setLocHelp((m) => ({ ...m, busy: false, attempted: true, reason: pos.reason || m.reason }));
    }
  };

  // "Turn on location" banner button — in the "prompt" state this fires the
  // browser's NATIVE permission pop-up (same as first-time site use). If the
  // user already denied it, the native prompt can't reappear, so we open the
  // step-by-step help dialog instead.
  const enableLocation = async () => {
    const pos = await getCurrentPosition();
    if (pos.latitude != null && pos.longitude != null) {
      setLocPerm("granted");
      toast.success("Location is on — you're good to clock in and start duty.");
    } else {
      if (pos.reason === "denied") setLocPerm("denied");
      setLocHelp({ open: true, reason: pos.reason || "unavailable", busy: false, attempted: false, action: "clock in", retry: null });
    }
  };

  const performStart = async (tail, taskTypeId, notes, capexCode, capexNote) => {
    setStartingTail(tail);
    try {
      // Location is MANDATORY for clock-ins — block and open the help
      // pop-up when GPS is off, denied, or unavailable.
      const pos = await getCurrentPosition();
      if (pos.latitude == null || pos.longitude == null) {
        setStartingTail(null);
        setLocHelp({
          open: true, reason: pos.reason || "unavailable", busy: false, attempted: false,
          action: "clock in",
          retry: () => performStart(tail, taskTypeId, notes, capexCode, capexNote),
        });
        return;
      }
      const payload = {
        aircraft_tail: tail,
        is_ot: mode.is_ot,
        is_acting_crew_chief: mode.is_acting_crew_chief,
        is_work_on_stat: mode.is_work_on_stat,
        stat_option: mode.is_work_on_stat ? statOption : null,
        task_type: taskTypeId,
        notes: notes || null,
        capex_code: capexCode || null,
        capex_note: capexNote || null,
        client_latitude: pos.latitude,
        client_longitude: pos.longitude,
        geofence_bypass_reason: null,
      };
      const { data } = await sendMutation("POST", "/clock/start", payload);
      setActive(data);
      window.dispatchEvent(new Event("st:ping-now"));
      const modeLabel = mode.is_work_on_stat ? `Stat · ${statOption}`
        : mode.is_ot && mode.is_acting_crew_chief ? "Acting CC OT"
        : mode.is_ot ? "OT"
        : mode.is_acting_crew_chief ? "Acting CC"
        : "Regular";
      const taskLabel = taskTypeId ? ` · ${(taskTypes.find((t) => t.id === taskTypeId)?.name) || ""}` : "";
      toast.success(`Clocked in on ${tail} · ${modeLabel}${taskLabel}`);
      if (data?.geofence_bypassed && data.geofence_reason === "outside" && data.geofence_distance_km != null) {
        toast.warning(`Clock-in flagged: you're ${data.geofence_distance_km} km from your city center. Session marked for admin review.`);
      }
    } catch (e) {
      if (e?.queued) {
        toast.info(`Offline · clock-in on ${tail} queued. Will sync when back online.`);
      } else {
        toast.error(e?.response?.data?.detail || "Failed to start");
      }
    } finally {
      setStartingTail(null);
    }
  };

  const confirmTaskStart = async () => {
    if (!taskModal.task_type) { toast.error("Pick a task type"); return; }
    // CAPEX task type requires a code + a non-blank note (both go to the
    // exported .xlsm — code to col AV, note to col AW).
    if (taskModal.task_type === "capex") {
      if (!taskModal.capex_code) { toast.error("Pick a CAPEX Account Code from the dropdown."); return; }
      if (!(taskModal.capex_note || "").trim()) { toast.error("A CAPEX note is required — describe the work briefly."); return; }
    }
    const { tail, task_type, notes, capex_code, capex_note } = taskModal;
    setTaskModal({
      open: false, tail: null, task_type: "", notes: "",
      capex_code: null, capex_note: "",
      capex_codes: [], capex_codes_loading: false, capex_search: "",
    });
    await performStart(tail, task_type, notes, capex_code, capex_note);
  };

  const stop = async () => {
    // Historical UX popped up a "note for Admin/Training balance" modal
    // whenever the day was about to fall below max_daily_hours. This has
    // been replaced by the persistent `acNote` field on the dashboard —
    // whatever's typed there is attached automatically as the shortfall
    // note. Empty note is fine — the AC auto-fill still fires.
    const note = (acNote || "").trim();
    await doStop(note || null);
  };

  const doStop = async (shortfallNote) => {
    setLoading(true);
    try {
      // Capture stop-side GPS so the shift log can flag out-of-radius
      // clock-outs (mirrors the /clock/start geofence audit).
      const pos = await getCurrentPosition();
      const stopPayload = {
        client_latitude: pos.latitude ?? null,
        client_longitude: pos.longitude ?? null,
        geofence_bypass_reason: pos.reason || null,
      };
      if (shortfallNote) stopPayload.shortfall_note = shortfallNote;
      const { data } = await sendMutation("POST", "/clock/stop", stopPayload);
      setActive(null);
      toast.success(`Clocked out · ${minutesToHours(data.duration_minutes)}h on ${data.aircraft_tail || "Office"}`);
      const seS = await api.get(`/clock/sessions?period=${currentPeriod.period_number}`);
      setSessions(seS.data || []);
      if (data.stop_geofence_bypassed && data.stop_geofence_reason === "outside" && data.stop_geofence_distance_km != null) {
        toast.warning(`Stop flagged: you're ${data.stop_geofence_distance_km} km from your city center.`);
      }
    } catch (e) {
      if (e?.queued) {
        // Optimistic UI — assume the clock-out will sync eventually. Clear
        // the active card locally so the engineer isn't stuck holding a stale
        // "still clocked in" state on the phone.
        setActive(null);
        toast.info("Offline · clock-out queued. Will sync when back online.");
      } else {
        toast.error(e?.response?.data?.detail || "Failed to stop");
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // Duty session — lightweight "I'm on shift" marker (no clock session,
  // no hours). Stamps a shift-log entry with GPS + timestamp. Auto-closes
  // after `user.max_daily_hours` elapse.
  // ---------------------------------------------------------------------
  const startDuty = async () => {
    setDutyBusy(true);
    try {
      const pos = await getCurrentPosition();
      // Location is MANDATORY for duty — block the start when GPS is off,
      // denied, or unavailable, and open the help pop-up.
      if (pos.latitude == null || pos.longitude == null) {
        setLocHelp({
          open: true, reason: pos.reason || "unavailable", busy: false, attempted: false,
          action: "start duty",
          retry: () => startDuty(),
        });
        return;
      }
      const { data } = await api.post("/duty/start", {
        client_latitude: pos.latitude,
        client_longitude: pos.longitude,
        geofence_bypass_reason: null,
      });
      setDuty(data);
      window.dispatchEvent(new Event("st:ping-now"));
      toast.success("On duty. Shift log stamped.");
      if (data.geofence_bypassed && data.geofence_reason === "outside" && data.geofence_distance_km != null) {
        toast.warning(`Flagged: you're ${data.geofence_distance_km} km from your city center.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start duty");
    } finally {
      setDutyBusy(false);
    }
  };

  const stopDuty = async () => {
    setDutyBusy(true);
    try {
      const pos = await getCurrentPosition();
      await api.post("/duty/stop", {
        client_latitude: pos.latitude ?? null,
        client_longitude: pos.longitude ?? null,
      });
      setDuty(null);
      toast.success("Duty ended.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to stop duty");
    } finally {
      setDutyBusy(false);
    }
  };



  const submitWholeDay = async () => {
    setWholeDayModal((m) => ({ ...m, submitting: true }));
    try {
      // Capture GPS so "Log whole day" gets the same geofence audit as
      // aircraft clock-ins — otherwise Supervisor/Manager/Senior-Manager
      // roles (who lean heavily on this flow) would be invisible to the
      // shift log.
      const pos = await getCurrentPosition();
      const wdResp = await sendMutation("POST", "/clock/whole-day", {
        notes: wholeDayModal.notes || null,
        is_ot: !!mode.is_ot,
        shortfall_note: (acNote || "").trim() || null,
        client_latitude: pos.latitude ?? null,
        client_longitude: pos.longitude ?? null,
        geofence_bypass_reason: pos.reason || null,
      });
      toast.success(
        mode.is_ot
          ? `Whole day logged as OT · ${user.max_daily_hours}h to OT (AR)`
          : `Whole day logged · ${user.max_daily_hours}h to Admin/Training`
      );
      const wdData = wdResp?.data || wdResp;
      if (wdData?.geofence_bypassed && wdData?.geofence_reason === "outside" && wdData?.geofence_distance_km != null) {
        toast.warning(`Flagged: you're ${wdData.geofence_distance_km} km from your city center.`);
      }
      setWholeDayModal({ open: false, notes: "", submitting: false });
      const seS = await api.get(`/clock/sessions?period=${currentPeriod.period_number}`);
      setSessions(seS.data || []);
    } catch (e) {
      if (e?.queued) {
        toast.info("Offline · whole-day queued. Will sync when back online.");
        setWholeDayModal({ open: false, notes: "", submitting: false });
      } else if (e?.response?.status === 409 && e?.response?.data?.detail?.code === "already_logged_today") {
        // User already has hours today — prompt for OT confirmation.
        const det = e.response.data.detail;
        const notes = wholeDayModal.notes;
        setWholeDayModal({ open: false, notes: "", submitting: false });
        setOtConfirm({
          open: true,
          existing_hours: Number(det.existing_hours || 0),
          would_add_hours: Number(det.would_add_hours || user.max_daily_hours || 8),
          notes,
          submitting: false,
        });
      } else {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : detail?.message || "Failed";
        toast.error(msg);
        setWholeDayModal((m) => ({ ...m, submitting: false }));
      }
    }
  };

  // Resubmits /clock/whole-day with is_ot=true after the user acknowledges
  // that the additional hours will be booked as Overtime.
  const confirmOtWholeDay = async () => {
    setOtConfirm((m) => ({ ...m, submitting: true }));
    try {
      const pos = await getCurrentPosition();
      await sendMutation("POST", "/clock/whole-day", {
        notes: otConfirm.notes || null,
        is_ot: true,
        shortfall_note: (acNote || "").trim() || null,
        client_latitude: pos.latitude ?? null,
        client_longitude: pos.longitude ?? null,
        geofence_bypass_reason: pos.reason || null,
      });
      toast.success(`Whole day logged as OT · ${otConfirm.would_add_hours}h`);
      setOtConfirm({ open: false, existing_hours: 0, would_add_hours: 0, notes: "", submitting: false });
      const seS = await api.get(`/clock/sessions?period=${currentPeriod.period_number}`);
      setSessions(seS.data || []);
    } catch (e) {
      if (e?.queued) {
        toast.info("Offline · OT whole-day queued. Will sync when back online.");
        setOtConfirm({ open: false, existing_hours: 0, would_add_hours: 0, notes: "", submitting: false });
      } else {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : detail?.message || "Failed";
        toast.error(msg);
        setOtConfirm((m) => ({ ...m, submitting: false }));
      }
    }
  };

  const submitTimeOff = async () => {
    if (!timeOffModal.start_date || !timeOffModal.end_date) { toast.error("Pick a date range"); return; }
    if (timeOffModal.end_date < timeOffModal.start_date) { toast.error("End date must be after start date"); return; }
    setTimeOffModal((m) => ({ ...m, submitting: true }));
    try {
      const payload = {
        type: timeOffModal.type,
        start_date: timeOffModal.start_date,
        end_date: timeOffModal.end_date,
        notes: timeOffModal.notes || null,
      };
      const h = parseFloat(timeOffModal.hours);
      if (!Number.isNaN(h) && h > 0) payload.hours = h;
      const { data } = await api.post("/time-off", payload);
      const labels = { vacation: "Vacation", sick: "Sick day", day_off: "Day off" };
      toast.success(`${labels[timeOffModal.type]} logged · ${data.created} day${data.created === 1 ? "" : "s"}`);
      const today = new Date().toISOString().slice(0, 10);
      setTimeOffModal({ open: false, type: "vacation", start_date: today, end_date: today, notes: "", hours: "", submitting: false });
      const seS = await api.get(`/clock/sessions?period=${currentPeriod.period_number}`);
      setSessions(seS.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setTimeOffModal((m) => ({ ...m, submitting: false }));
    }
  };

  const groupedAircraft = useMemo(() => {
    const g = {};
    aircraft.forEach((a) => {
      g[a.fleet] = g[a.fleet] || [];
      g[a.fleet].push(a);
    });
    return g;
  }, [aircraft]);

  // Favourites — user-pinned aircraft. Rendered as a dedicated pinned group
  // at the top of the aircraft grid so engineers can clock in on their most
  // frequently-used tails in one tap.
  const favouriteTails = useMemo(
    () => new Set(user?.favourite_aircraft || []),
    [user?.favourite_aircraft],
  );
  const favouriteAircraft = useMemo(
    () => aircraft.filter((a) => favouriteTails.has(a.tail)),
    [aircraft, favouriteTails],
  );

  const toggleFavourite = async (tail) => {
    if (!tail) return;
    const isFav = favouriteTails.has(tail);
    try {
      if (isFav) {
        await api.delete(`/users/me/favourites/${encodeURIComponent(tail)}`);
        toast.success(`Unpinned ${tail}`);
      } else {
        await api.post(`/users/me/favourites/${encodeURIComponent(tail)}`);
        toast.success(`Pinned ${tail} to favourites`);
      }
      // Refresh the user record so the star + favourites group re-render.
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update favourite.");
    }
  };

  const totals = useMemo(() => {
    const completed = sessions.filter((s) => s.ended_at);
    const totalMin = completed.reduce((a, s) => a + (s.duration_minutes || 0), 0);
    const byAircraft = {};
    completed.forEach((s) => {
      byAircraft[s.aircraft_tail] = (byAircraft[s.aircraft_tail] || 0) + (s.duration_minutes || 0);
    });
    return { totalHours: minutesToHours(totalMin), sessionCount: completed.length, byAircraft };
  }, [sessions]);

  const profileIncomplete = !user?.location || !user?.city;

  // Extracted so we can render the same card in both the Favourites group and
  // the fleet groups.
  const renderAircraftCard = (a) => {
    const mins = totals.byAircraft[a.tail] || 0;
    const isActive = active?.aircraft_tail === a.tail;
    const status = a.status || "normal";
    const nonNormal = status !== "normal";
    const statusPill = status === "heavy_check"
      ? { label: "Heavy Check", cls: "bg-blue-100 text-blue-800 border-blue-200" }
      : status === "capex"
        ? { label: "CAPEX", cls: "bg-rose-100 text-rose-800 border-rose-200" }
        : null;
    const isFav = favouriteTails.has(a.tail);
    return (
      <div
        key={a.tail}
        className={`relative border ${
          isActive ? "border-emerald-500 bg-emerald-50/50"
          : status === "heavy_check" ? "border-blue-200 bg-blue-50/30"
          : nonNormal ? "border-amber-200 bg-amber-50/30"
          : "border-summit-hairline bg-white"
        } rounded-md p-4 card-hover`}
        data-testid={`aircraft-card-${a.tail}`}
      >
        <button
          type="button"
          onClick={() => toggleFavourite(a.tail)}
          className={`absolute top-2 right-2 p-1 rounded transition-colors ${
            isFav ? "text-amber-500 hover:text-amber-600" : "text-gray-300 hover:text-amber-500"
          }`}
          title={isFav ? "Unpin from favourites" : "Pin to favourites"}
          data-testid={`favourite-toggle-${a.tail}`}
          aria-label={isFav ? `Unpin ${a.tail}` : `Pin ${a.tail}`}
        >
          <Star className={`w-4 h-4 ${isFav ? "fill-amber-400" : ""}`} strokeWidth={2} />
        </button>
        <div className="flex items-center justify-between pr-6">
          <div className="font-mono text-sm tracking-tight">{a.tail}</div>
          {isActive && <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full dot-pulse" />}
        </div>
        {statusPill && (
          <div className="mt-1.5">
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-mono text-[9px] uppercase tracking-widest ${statusPill.cls}`}
              data-testid={`aircraft-status-badge-${a.tail}`}
              title="A CAPEX Account Code is required to clock in on this aircraft."
            >
              <Wrench className="w-2.5 h-2.5" />
              {statusPill.label}
            </span>
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mt-1">
          {minutesToHours(mins)}h this period
        </div>
        <Button
          size="sm"
          variant={isActive ? "secondary" : "default"}
          className={`mt-3 w-full text-xs h-8 ${isActive ? "" : "bg-summit-primary hover:bg-summit-primary-soft text-white"}`}
          onClick={() => (isActive ? stop() : start(a.tail))}
          disabled={(!!active && !isActive) || startingTail === a.tail || profileIncomplete}
          data-testid={`clock-toggle-${a.tail}`}
        >
          {isActive ? (
            <><Square className="w-3 h-3 mr-1" /> Stop</>
          ) : (
            <><Play className="w-3 h-3 mr-1" /> Start</>
          )}
        </Button>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
      <InstallHint />
      <PostInstallPushPrompt />
      {profileIncomplete && (
        <div className="mb-6 flex items-center gap-3 p-4 border border-amber-300 bg-amber-50 rounded-md" data-testid="profile-incomplete-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
          <div className="flex-1 text-sm text-amber-900">
            {!user?.location && !user?.city
              ? "Your Location of Work and City are missing. Set both in your profile before clocking in."
              : !user?.location
                ? "Your Location of Work is missing. Set it in your profile before clocking in."
                : "Your City is missing. Set it in your profile before clocking in."}
          </div>
          <Link to="/profile" className="text-sm font-medium underline">Go to profile</Link>
        </div>
      )}

      {locPerm && locPerm !== "granted" && (
        <div className="mb-6 flex flex-wrap items-center gap-3 p-4 border border-sky-300 bg-sky-50 rounded-md" data-testid="location-off-banner">
          <MapPin className="w-4 h-4 text-sky-700 shrink-0" />
          <div className="flex-1 min-w-[200px] text-sm text-sky-900">
            {locPerm === "denied"
              ? "Location access is blocked for this app — you'll need it to clock in or start duty."
              : "Location is off — you'll need it to clock in or start duty."}
          </div>
          <Button
            size="sm"
            className="bg-sky-700 hover:bg-sky-800 text-white h-8 text-xs"
            onClick={enableLocation}
            data-testid="location-off-banner-enable"
          >
            <MapPin className="w-3 h-3 mr-1" /> Turn on location
          </Button>
        </div>
      )}

      {todayStatName && !profileIncomplete && (
        <div className="mb-6 flex items-center gap-3 p-4 border border-rose-300 bg-rose-50 rounded-md" data-testid="today-stat-banner">
          <Sun className="w-4 h-4 text-rose-700" />
          <div className="flex-1 text-sm text-rose-900">
            <span className="font-medium">Today is {todayStatName}</span> — a stat holiday for {user?.location}.
            Only <span className="font-mono uppercase tracking-widest text-[11px] px-1.5 py-0.5 bg-rose-100 rounded">Work on Stat</span> clock-in is available. Pick AN / AO / AP / AS below.
          </div>
        </div>
      )}

      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Dashboard · MST</div>
          <h1 className="font-display text-2xl md:text-5xl font-semibold tracking-tight mt-1">
            {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.display_name}
          </h1>
        </div>
        {currentPeriod && (
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Pay Period {currentPeriod.period_number}</div>
            <div className="text-xs md:text-sm font-medium">{currentPeriod.range_label}</div>
          </div>
        )}
      </div>

      {/* Timer card */}
      <div className={`mt-6 md:mt-8 grid lg:grid-cols-5 gap-6`}>
        <div
          className={`lg:col-span-3 relative border border-summit-hairline rounded-lg p-8 bg-white ${active ? "timer-active" : ""}`}
          data-testid="timer-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${active ? "bg-emerald-500 dot-pulse" : "bg-gray-300"}`} />
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                {active ? "Clocked in" : "Idle"}
              </span>
            </div>
            {active && (
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 font-mono">
                {active.aircraft_tail} · {active.fleet}
                {active.is_work_on_stat ? " · Stat" : active.is_ot && active.is_acting_crew_chief ? " · ACC OT" : active.is_ot ? " · OT" : active.is_acting_crew_chief ? " · ACC" : ""}
              </Badge>
            )}
          </div>
          <div className="mt-6">
            {active ? (
              <LiveTimer startedAt={active.started_at} large testId="active-timer" />
            ) : (
              <div>
                <div className="font-mono text-6xl md:text-7xl tracking-tighter text-gray-300">00:00:00</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mt-2">
                  Select an aircraft below to start
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 flex items-center gap-4 flex-wrap">
            {active ? (
              <Button onClick={stop} disabled={loading} className="bg-summit-primary hover:bg-summit-primary-soft" data-testid="stop-clock-btn">
                <Square className="w-4 h-4 mr-1.5" /> Clock out
              </Button>
            ) : (
              <span className="font-mono text-[11px] uppercase tracking-widest text-gray-400">
                No active session
              </span>
            )}
            {!active && user?.location && (
              <Button
                onClick={() => setWholeDayModal({ open: true, notes: "", submitting: false })}
                variant="outline"
                className="border-summit-primary text-summit-ink hover:bg-summit-glacier"
                data-testid="whole-day-btn"
              >
                <Sunrise className="w-4 h-4 mr-1.5" /> Log whole day ({user.max_daily_hours}h)
              </Button>
            )}
            {/* Start / Stop duty — lightweight on-shift marker (no hours). */}
            {!duty ? (
              <Button
                onClick={startDuty}
                disabled={dutyBusy}
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid="start-duty-btn"
              >
                <Play className="w-4 h-4 mr-1.5" /> {dutyBusy ? "Starting…" : "Start duty"}
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  onClick={stopDuty}
                  disabled={dutyBusy}
                  variant="outline"
                  className="border-rose-500 text-rose-700 hover:bg-rose-50"
                  data-testid="stop-duty-btn"
                >
                  <Square className="w-4 h-4 mr-1.5" /> {dutyBusy ? "Stopping…" : "Stop duty"}
                </Button>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-mono ${
                    duty.geofence_bypassed
                      ? "border-amber-300 bg-amber-50 text-amber-800"
                      : "border-emerald-200 bg-emerald-50 text-emerald-800"
                  }`}
                  data-testid="duty-status-chip"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                  On duty since {new Date(duty.started_at).toLocaleTimeString("en-CA", {
                    hour: "2-digit", minute: "2-digit", timeZone: "America/Denver",
                  })} MST
                  {duty.geofence_distance_km != null && (
                    <span className="opacity-70">· {duty.geofence_distance_km} km</span>
                  )}
                </span>
              </div>
            )}
            {!active && (
              <Button
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  setTimeOffModal({ open: true, type: "vacation", start_date: today, end_date: today, notes: "", hours: "", submitting: false });
                }}
                variant="outline"
                className="border-summit-hairline text-summit-primary-soft hover:bg-summit-glacier"
                data-testid="time-off-btn"
              >
                <CalendarOff className="w-4 h-4 mr-1.5" /> Log time off
              </Button>
            )}
            {user?.max_daily_hours ? (
              <span className="font-mono text-[10px] text-gray-400" data-testid="auto-close-hint">
                Auto-closes after {user.max_daily_hours === 8 ? 9 : user.max_daily_hours}h
              </span>
            ) : null}
          </div>
        </div>

        {/* KPI side — desktop only. On mobile these live in the /timesheet tab. */}
        <div className="hidden md:grid lg:col-span-2 grid-cols-2 gap-4">
          <div className="border border-summit-hairline rounded-lg p-5 bg-white">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Period Hours</div>
            <div className="font-mono text-4xl mt-2 tracking-tighter" data-testid="period-hours">{totals.totalHours}</div>
            <div className="font-mono text-[10px] text-gray-400 mt-1">{totals.sessionCount} sessions</div>
          </div>
          <div className="border border-summit-hairline rounded-lg p-5 bg-white">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Location</div>
            <div className="text-base font-medium mt-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>{user?.location || "—"}</div>
            <div className="font-mono text-[10px] text-gray-400 mt-1">Per profile</div>
          </div>
        </div>
      </div>

      {/* Aircraft grid */}
      <div className="mt-12">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-2xl font-medium tracking-tight">Aircraft</h2>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{aircraft.length} tail numbers</span>
        </div>

        {/* Clock-in mode selector */}
        <div className="mt-4 flex flex-wrap items-center gap-2 p-3 border border-dashed border-summit-hairline rounded-md bg-white" data-testid="clock-mode-selector">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mr-2">Next clock-in:</span>
          <button
            type="button"
            disabled={!!active || !!todayStatName}
            onClick={() => setMode({ is_ot: false, is_acting_crew_chief: false, is_work_on_stat: false })}
            className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
              !mode.is_ot && !mode.is_acting_crew_chief && !mode.is_work_on_stat
                ? "bg-summit-primary text-white border-summit-primary"
                : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="mode-regular"
            title={todayStatName ? "Today is a stat holiday — only Work on Stat is allowed" : undefined}
          >Regular</button>
          <button
            type="button"
            disabled={!!active || !!todayStatName}
            onClick={() => setMode((m) => ({ ...m, is_ot: !m.is_ot, is_work_on_stat: false }))}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
              mode.is_ot && !mode.is_work_on_stat
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="mode-ot"
            title={todayStatName ? "Today is a stat holiday — only Work on Stat is allowed" : undefined}
          ><Zap className="w-3 h-3" /> OT · AR</button>
          <button
            type="button"
            disabled={!!active || !!todayStatName}
            onClick={() => setMode((m) => ({ ...m, is_acting_crew_chief: !m.is_acting_crew_chief, is_work_on_stat: false }))}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
              mode.is_acting_crew_chief && !mode.is_work_on_stat
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="mode-acc"
            title={todayStatName ? "Today is a stat holiday — only Work on Stat is allowed" : undefined}
          ><Star className="w-3 h-3" /> Acting CC · AH</button>
          <button
            type="button"
            disabled={!!active}
            onClick={() => setMode((m) => ({ is_ot: false, is_acting_crew_chief: false, is_work_on_stat: !m.is_work_on_stat }))}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
              mode.is_work_on_stat
                ? "bg-rose-600 text-white border-rose-600"
                : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
            } disabled:opacity-40`}
            data-testid="mode-work-on-stat"
          ><Sun className="w-3 h-3" /> Work on Stat · AS</button>
          {!!active && (
            <span className="font-mono text-[10px] text-gray-400 ml-2">Stop current session to change mode</span>
          )}
          {mode.is_work_on_stat && !active && (
            <div className="w-full mt-2 pt-2 border-t border-summit-hairline flex flex-wrap items-center gap-2" data-testid="stat-option-selector">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mr-2">Capture in column:</span>
              {statOptions.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => setStatOption(opt.code)}
                  className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
                    statOption === opt.code
                      ? "bg-rose-600 text-white border-rose-600"
                      : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
                  }`}
                  data-testid={`stat-option-${opt.code}`}
                  title={opt.label}
                >{opt.code} · {opt.label}</button>
              ))}
            </div>
          )}
        </div>

        {/* Persistent Admin/Training balance note — attached automatically to
            any clock-out where the day falls below max_daily_hours. Replaces
            the modal prompt on stop. Persisted in localStorage per user. */}
        <div className="mt-3 p-3 border border-dashed border-summit-hairline rounded-md bg-white" data-testid="ac-note-panel">
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-0" htmlFor="ac-note">
              Admin/Training balance note
            </Label>
            <span className="font-mono text-[10px] text-gray-400">Auto-applied on clock-out</span>
          </div>
          <Textarea
            id="ac-note"
            value={acNote}
            onChange={(e) => persistAcNote(e.target.value)}
            placeholder="e.g. Toolbox meeting · admin paperwork · training call"
            rows={2}
            className="text-xs resize-none"
            data-testid="ac-note-input"
          />
        </div>

        <div className="mt-5 space-y-6">
          {/* Favourites — user-pinned tails at the top for quick clock-in. */}
          {favouriteAircraft.length > 0 && (
            <div data-testid="favourites-group">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />
                <h3 className="font-display text-sm uppercase tracking-widest text-gray-700">Favourites</h3>
                <div className="h-px bg-amber-200 flex-1" />
                <span className="font-mono text-[10px] text-gray-400">{favouriteAircraft.length} pinned</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {favouriteAircraft.map((a) => renderAircraftCard(a))}
              </div>
            </div>
          )}

          {[...fleetOrder, ...Object.keys(groupedAircraft).filter((f) => !fleetOrder.includes(f))]
            .filter((fleet) => (groupedAircraft[fleet] || []).length > 0)
            .map((fleet) => (
            <div key={fleet}>
              <div className="flex items-center gap-2 mb-2">
                <Plane className="w-3 h-3 text-gray-400" />
                <h3 className="font-display text-sm uppercase tracking-widest text-gray-600">{fleet}</h3>
                <div className="h-px bg-gray-200 flex-1" />
                <span className="font-mono text-[10px] text-gray-400">{(groupedAircraft[fleet] || []).length} ac</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {(groupedAircraft[fleet] || []).map((a) => renderAircraftCard(a))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent sessions — desktop only. On mobile these live in the /timesheet tab. */}
      <div className="hidden md:block mt-12">
        <h2 className="font-display text-2xl font-medium tracking-tight">Recent sessions</h2>
        <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-x-auto" data-testid="recent-sessions">
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr className="border-b border-summit-hairline text-gray-500">
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Aircraft</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Type</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Started</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Ended</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Location</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-400 text-sm">No sessions this period yet.</td></tr>
              )}
              {sessions.slice(0, 15).map((s) => {
                const tag = s.is_whole_day ? { label: "Whole Day", cls: "bg-sky-100 text-sky-700" }
                  : s.is_work_on_stat ? { label: "Stat", cls: "bg-rose-100 text-rose-700" }
                  : s.is_ot && s.is_acting_crew_chief ? { label: "ACC OT", cls: "bg-indigo-100 text-indigo-700" }
                  : s.is_ot ? { label: "OT", cls: "bg-amber-100 text-amber-800" }
                  : s.is_acting_crew_chief ? { label: "ACC", cls: "bg-indigo-100 text-indigo-700" }
                  : { label: "Regular", cls: "bg-gray-100 text-gray-700" };
                return (
                <tr key={s.id} className="border-b border-summit-hairline last:border-0">
                  <td className="px-4 py-2.5 font-mono">{s.aircraft_tail || "OFFICE"}</td>
                  <td className="px-4 py-2.5"><Badge className={`${tag.cls} hover:${tag.cls} font-mono text-[10px]`}>{tag.label}</Badge></td>
                  <td className="px-4 py-2.5">{formatMSTDateTime(s.started_at)}</td>
                  <td className="px-4 py-2.5">{s.ended_at ? formatMSTDateTime(s.ended_at) : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{s.location || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-right">{s.ended_at ? minutesToHours(s.duration_minutes) : "—"}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* CAPEX Account Code picker — required when the aircraft is on Heavy
          Check or Down for CAPEX. Value lands in column AV of the exported
          .xlsm for the day of the session. */}
      <Dialog
        open={capexModal.open}
        onOpenChange={(o) => !o && setCapexModal({ open: false, tail: null, status: null, codes: [], loading: false, choice: "", search: "", note: "", mandatoryReason: "aircraft-status" })}
      >
        <DialogContent className="max-w-2xl" data-testid="capex-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-4 h-4" /> Select CAPEX Account Code
            </DialogTitle>
            <DialogDescription>
              {capexModal.tail && (
                <>
                  <span className="font-mono font-medium text-summit-ink">{capexModal.tail}</span>{" "}
                  {capexModal.mandatoryReason === "manual-toggle" ? (
                    <>
                      · <span className="inline-block px-1.5 rounded font-mono text-[10px] uppercase tracking-widest bg-summit-glacier text-summit-primary">CAPEX work</span>{" "}
                      selected manually. Pick the item you&apos;re working on — the code lands in column AV and your note lands in column AW of your timesheet.
                    </>
                  ) : (
                    <>
                      is currently on{" "}
                      <span className={`inline-block px-1.5 rounded font-mono text-[10px] uppercase tracking-widest ${
                        capexModal.status === "capex"
                          ? "bg-rose-100 text-rose-800"
                          : "bg-amber-100 text-amber-800"
                      }`}>
                        {capexModal.status === "capex" ? "Down for CAPEX" : "Heavy Check"}
                      </span>
                      . Pick the item you&apos;re working on — the code lands in column AV and your note lands in column AW of your timesheet.
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            <Input
              value={capexModal.search}
              onChange={(e) => setCapexModal((m) => ({ ...m, search: e.target.value }))}
              placeholder={`Search ${capexModal.codes.length} code${capexModal.codes.length === 1 ? "" : "s"}…`}
              className="pl-8"
              data-testid="capex-search-input"
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto border border-summit-hairline rounded-md divide-y divide-summit-hairline" data-testid="capex-list">
            {capexModal.loading && (
              <div className="p-6 text-center font-mono text-[11px] text-gray-400 uppercase tracking-widest">Loading codes…</div>
            )}
            {!capexModal.loading && capexModal.codes.length === 0 && (
              <div className="p-6 text-center font-mono text-[11px] text-gray-400 uppercase tracking-widest">
                No CAPEX codes available for this aircraft
              </div>
            )}
            {capexModal.codes
              .filter((c) => c.name.toLowerCase().includes(capexModal.search.toLowerCase()))
              .map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCapexModal((m) => ({ ...m, choice: c.id }))}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-start gap-2 ${
                    capexModal.choice === c.id
                      ? "bg-summit-primary text-white"
                      : "bg-white text-gray-800 hover:bg-summit-glacier"
                  }`}
                  data-testid={`capex-option-${c.id}`}
                >
                  <span className={`mt-0.5 shrink-0 w-3 h-3 rounded-full border ${
                    capexModal.choice === c.id ? "bg-white border-white" : "border-gray-300"
                  }`} />
                  <span className="flex-1">
                    <span className="block">{c.name}</span>
                    {c.is_generic && (
                      <span className={`mt-1 inline-block px-1.5 py-0.5 rounded font-mono text-[9px] uppercase tracking-widest ${
                        capexModal.choice === c.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                      }`}>
                        Generic
                      </span>
                    )}
                  </span>
                </button>
              ))}
          </div>
          <div className="mt-2">
            <Label htmlFor="capex-note-input" className="text-xs uppercase tracking-widest text-gray-500">
              CAPEX note · required (goes to column AW)
            </Label>
            <Textarea
              id="capex-note-input"
              value={capexModal.note}
              onChange={(e) => setCapexModal((m) => ({ ...m, note: e.target.value }))}
              placeholder="e.g. Inspection of #2 engine mount bracket, snag write-up 12345…"
              className="mt-1"
              rows={2}
              data-testid="capex-note-input"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Notes describe the CAPEX work performed for this session. Last-note-per-day is transcribed to column AW of your exported .xlsm.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCapexModal({ open: false, tail: null, status: null, codes: [], loading: false, choice: "", search: "", note: "", mandatoryReason: "aircraft-status" })}
            >Cancel</Button>
            <Button
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              onClick={confirmCapex}
              disabled={!capexModal.choice || !(capexModal.note || "").trim()}
              data-testid="capex-confirm-btn"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task-type modal (Regular / OT / Acting CC on aircraft) */}
      <Dialog open={taskModal.open} onOpenChange={(o) => !o && setTaskModal({ open: false, tail: null, task_type: "", notes: "", capex_code: null, capex_note: "", capex_codes: [], capex_codes_loading: false, capex_search: "" })}>
        <DialogContent className="max-w-lg" data-testid="task-type-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Select task type</DialogTitle>
            <DialogDescription>
              {taskModal.tail ? `Starting session on ${taskModal.tail}. Pick the task you're performing.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {taskTypes.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTaskModal((m) => ({ ...m, task_type: t.id }));
                  if (t.id === "capex" && (taskModal.capex_codes || []).length === 0) {
                    loadCapexCodesForTaskModal(taskModal.tail);
                  }
                }}
                className={`w-full text-left px-3 py-2 rounded-md border font-mono text-xs uppercase tracking-widest transition-colors flex items-center justify-between ${
                  taskModal.task_type === t.id
                    ? "bg-summit-primary text-white border-summit-primary"
                    : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
                }`}
                data-testid={`task-option-${t.id}`}
              >
                <span>{t.name}</span>
                {t.id === "capex" && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${taskModal.task_type === t.id ? "bg-white/20 text-white" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                    AV / AW
                  </span>
                )}
              </button>
            ))}
            {taskModal.task_type === "capex" && (
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-3 space-y-2" data-testid="task-capex-panel">
                <Label className="text-xs uppercase tracking-widest text-emerald-800">CAPEX Account Code · required</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                  <Input
                    value={taskModal.capex_search || ""}
                    onChange={(e) => setTaskModal((m) => ({ ...m, capex_search: e.target.value }))}
                    placeholder={taskModal.capex_codes_loading ? "Loading codes…" : `Search ${(taskModal.capex_codes || []).length} code${(taskModal.capex_codes || []).length === 1 ? "" : "s"}…`}
                    className="pl-8"
                    data-testid="task-capex-search"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border border-summit-hairline rounded-md divide-y divide-summit-hairline bg-white" data-testid="task-capex-list">
                  {taskModal.capex_codes_loading && (
                    <div className="p-4 text-center font-mono text-[11px] text-gray-400 uppercase tracking-widest">Loading…</div>
                  )}
                  {!taskModal.capex_codes_loading && (taskModal.capex_codes || []).length === 0 && (
                    <div className="p-4 text-center font-mono text-[11px] text-gray-400 uppercase tracking-widest">No CAPEX codes available for this aircraft</div>
                  )}
                  {(taskModal.capex_codes || [])
                    .filter((c) => c.name.toLowerCase().includes((taskModal.capex_search || "").toLowerCase()))
                    .map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setTaskModal((m) => ({ ...m, capex_code: c.id }))}
                        className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-start gap-2 ${
                          taskModal.capex_code === c.id
                            ? "bg-emerald-700 text-white"
                            : "bg-white text-gray-800 hover:bg-emerald-50"
                        }`}
                        data-testid={`task-capex-option-${c.id}`}
                      >
                        <span className={`mt-0.5 shrink-0 w-3 h-3 rounded-full border ${taskModal.capex_code === c.id ? "bg-white border-white" : "border-gray-300"}`} />
                        <span className="flex-1">
                          <span className="block">{c.name}</span>
                          {c.is_generic && (
                            <span className={`mt-1 inline-block px-1.5 py-0.5 rounded font-mono text-[9px] uppercase tracking-widest ${taskModal.capex_code === c.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>Generic</span>
                          )}
                        </span>
                      </button>
                    ))}
                </div>
                <Label className="text-xs uppercase tracking-widest text-emerald-800 mt-2 block">CAPEX note · required (goes to column AW)</Label>
                <Textarea
                  value={taskModal.capex_note || ""}
                  onChange={(e) => setTaskModal((m) => ({ ...m, capex_note: e.target.value }))}
                  placeholder="e.g. Inspection of #2 engine mount bracket, snag write-up 12345…"
                  rows={2}
                  data-testid="task-capex-note-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Note (optional)</Label>
              <Textarea
                value={taskModal.notes}
                onChange={(e) => setTaskModal((m) => ({ ...m, notes: e.target.value }))}
                placeholder="Add context for this session…"
                rows={2}
                data-testid="task-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskModal({ open: false, tail: null, task_type: "", notes: "", capex_code: null, capex_note: "", capex_codes: [], capex_codes_loading: false, capex_search: "" })}>Cancel</Button>
            <Button
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              onClick={confirmTaskStart}
              disabled={taskModal.task_type === "capex" && (!taskModal.capex_code || !(taskModal.capex_note || "").trim())}
              data-testid="task-start-confirm"
            >
              Start on {taskModal.tail}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Whole-day modal (Office / Admin location only) */}
      <Dialog open={wholeDayModal.open} onOpenChange={(o) => !o && setWholeDayModal({ open: false, notes: "", submitting: false })}>
        <DialogContent className="max-w-md" data-testid="whole-day-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sunrise className="w-4 h-4" /> Log a whole day
              {mode.is_ot && (
                <span className="font-mono text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1.5 py-0.5" data-testid="whole-day-ot-badge">OT · AR</span>
              )}
            </DialogTitle>
            <DialogDescription>
              {mode.is_ot ? (
                <>This will log {user?.max_daily_hours}h as <strong>Overtime — OT / Draft Pay (AR)</strong> for today, matching your selected OT clock-in mode. Switch the mode back to Regular if these are normal office/admin hours.</>
              ) : (
                <>This will log {user?.max_daily_hours}h to Admin/Training (AC) for today. Use this instead of clocking
                individual sessions when you&apos;re working office / admin hours.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Note (optional)</Label>
            <Textarea
              value={wholeDayModal.notes}
              onChange={(e) => setWholeDayModal((m) => ({ ...m, notes: e.target.value }))}
              placeholder="Describe today's work…"
              rows={3}
              data-testid="whole-day-notes-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWholeDayModal({ open: false, notes: "", submitting: false })}>Cancel</Button>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitWholeDay} disabled={wholeDayModal.submitting} data-testid="whole-day-confirm">
              {wholeDayModal.submitting ? "Logging…" : `Log ${user?.max_daily_hours || 8}h${mode.is_ot ? " as OT" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overtime confirmation modal — shown when /clock/whole-day returns 409
          because the user already has hours logged today. */}
      <Dialog open={otConfirm.open} onOpenChange={(o) => !o && !otConfirm.submitting && setOtConfirm({ open: false, existing_hours: 0, would_add_hours: 0, notes: "", submitting: false })}>
        <DialogContent className="max-w-md" data-testid="whole-day-ot-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Overtime confirmation
            </DialogTitle>
            <DialogDescription>
              You&apos;ve already logged hours for today. Filling the day again will book the additional time
              as Overtime (OT).
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">Already logged today</span>
              <span className="font-semibold text-gray-900 tabular-nums" data-testid="whole-day-ot-existing">
                {otConfirm.existing_hours}h
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">This fill will add</span>
              <span className="font-semibold text-amber-800 tabular-nums" data-testid="whole-day-ot-adding">
                {otConfirm.would_add_hours}h · OT
              </span>
            </div>
            <div className="h-px bg-amber-200 my-1" />
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-800 font-medium">Projected total</span>
              <span className="font-bold text-gray-900 tabular-nums" data-testid="whole-day-ot-total">
                {(Number(otConfirm.existing_hours || 0) + Number(otConfirm.would_add_hours || 0)).toFixed(2).replace(/\.00$/, "")}h
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOtConfirm({ open: false, existing_hours: 0, would_add_hours: 0, notes: "", submitting: false })}
              disabled={otConfirm.submitting}
              data-testid="whole-day-ot-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmOtWholeDay}
              disabled={otConfirm.submitting}
              data-testid="whole-day-ot-confirm"
            >
              {otConfirm.submitting ? "Logging…" : "Proceed as Overtime"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Time-off modal (Day Off / Sick / Vacation — for all users) */}
      <Dialog open={timeOffModal.open} onOpenChange={(o) => !o && setTimeOffModal((m) => ({ ...m, open: false }))}>
        <DialogContent className="max-w-md" data-testid="time-off-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarOff className="w-4 h-4" /> Log time off</DialogTitle>
            <DialogDescription>
              Record a Day Off, Sick Day, or Vacation. Defaults to your daily hours ({user?.max_daily_hours || 8}h); enter a value below for a partial day.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500 mb-2 block">Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {["vacation", "sick", "day_off"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTimeOffModal((m) => ({ ...m, type: t }))}
                    className={`px-3 py-2 rounded-md border font-mono text-[10px] uppercase tracking-widest transition-colors ${
                      timeOffModal.type === t
                        ? "bg-summit-primary text-white border-summit-primary"
                        : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
                    }`}
                    data-testid={`time-off-type-${t}`}
                  >{t === "day_off" ? "Day Off" : t.charAt(0).toUpperCase() + t.slice(1)}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">From</Label>
                <input
                  type="date"
                  value={timeOffModal.start_date}
                  onChange={(e) => setTimeOffModal((m) => ({ ...m, start_date: e.target.value, end_date: m.end_date < e.target.value ? e.target.value : m.end_date }))}
                  className="w-full h-10 px-3 rounded-md border border-summit-hairline text-sm"
                  data-testid="time-off-start-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">To</Label>
                <input
                  type="date"
                  value={timeOffModal.end_date}
                  min={timeOffModal.start_date}
                  onChange={(e) => setTimeOffModal((m) => ({ ...m, end_date: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-summit-hairline text-sm"
                  data-testid="time-off-end-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Hours (optional)</Label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="24"
                value={timeOffModal.hours}
                onChange={(e) => setTimeOffModal((m) => ({ ...m, hours: e.target.value }))}
                placeholder={`Defaults to ${user?.max_daily_hours || 8}`}
                className="w-full h-10 px-3 rounded-md border border-summit-hairline text-sm"
                data-testid="time-off-hours-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Notes (optional)</Label>
              <Textarea
                value={timeOffModal.notes}
                onChange={(e) => setTimeOffModal((m) => ({ ...m, notes: e.target.value }))}
                rows={2}
                placeholder="Doctor appointment, family trip, personal day…"
                data-testid="time-off-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeOffModal((m) => ({ ...m, open: false }))}>Cancel</Button>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitTimeOff} disabled={timeOffModal.submitting} data-testid="time-off-submit">
              {timeOffModal.submitting ? "Logging…" : "Log"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location help pop-up — GPS off / denied / unavailable */}
      <Dialog open={locHelp.open} onOpenChange={(o) => { if (!o) setLocHelp((m) => ({ ...m, open: false })); }}>
        <DialogContent className="max-w-md" data-testid="location-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-summit-primary" />
              Turn on location to {locHelp.action}
            </DialogTitle>
            <DialogDescription>
              {locHelp.reason === "denied"
                ? "Location access for this app is currently blocked. Follow the steps below to allow it, then try again."
                : locHelp.reason === "unsupported"
                  ? "This device or browser doesn't support location. Try from your phone with location services turned on."
                  : "We couldn't get a GPS fix. Make sure location services are turned on, then try again."}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-summit-hairline bg-summit-glacier-soft/40 px-4 py-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">How to turn it on</div>
            {/iPhone|iPad|iPod/i.test(navigator.userAgent) ? (
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-700" data-testid="location-help-steps">
                <li>Open <span className="font-medium">Settings → Privacy &amp; Security → Location Services</span> and turn it <span className="font-medium">ON</span>.</li>
                <li>Scroll to <span className="font-medium">Safari</span> (or this app if installed to your Home Screen) and pick <span className="font-medium">“While Using the App”</span>.</li>
                <li>Come back here and tap <span className="font-medium">Try again</span>.</li>
              </ol>
            ) : /Android/i.test(navigator.userAgent) ? (
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-700" data-testid="location-help-steps">
                <li>Swipe down from the top and make sure <span className="font-medium">Location</span> is <span className="font-medium">ON</span> in quick settings.</li>
                <li>In Chrome (or this app), tap <span className="font-medium">⋮ → Settings → Site settings → Location</span> and choose <span className="font-medium">Allow</span>.</li>
                <li>Return here and tap <span className="font-medium">Try again</span>.</li>
              </ol>
            ) : (
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-700" data-testid="location-help-steps">
                <li>Click the <span className="font-medium">padlock / tune icon</span> in your browser&apos;s address bar.</li>
                <li>Set <span className="font-medium">Location</span> to <span className="font-medium">Allow</span> for this site.</li>
                <li>Tap <span className="font-medium">Try again</span> below.</li>
              </ol>
            )}
          </div>
          {locHelp.attempted && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="location-help-still-blocked">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Still couldn&apos;t get your location. Double-check the steps above — a page reload can help after changing browser permissions.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocHelp((m) => ({ ...m, open: false }))} data-testid="location-help-cancel">Cancel</Button>
            <Button
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              onClick={retryLocationHelp}
              disabled={locHelp.busy}
              data-testid="location-help-retry"
            >
              {locHelp.busy ? "Checking…" : "I've turned it on — try again"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
